import React, { useState, useContext, useEffect, useRef } from 'react';
import { UserContext } from './UserContext';
import { ApiService } from './ApiService';
import SocialAuth from './SocialAuth';
import FloatingError from './FloatingError';
import { ConnectionLoader } from './ConnectionMotion';
import {createPortal} from 'react-dom';
import './SignUpPrestige.css';
import API_BASE_URL from '../config';

function SignIn({ initiallyOpen = false, onClose } = {}) {
  const { setCurrentUser, setToken } = useContext(UserContext);
  const loginPending=useRef(false);
  const warmed=useRef(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSignInForm, setShowSignInForm] = useState(initiallyOpen);
  const [resetEmail, setResetEmail] = useState('');
  const [showReset, setShowReset] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [showVerifyNotice, setShowVerifyNotice] = useState(false);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('currentUser');
    if (storedToken && storedUser) {
      setToken(storedToken);
      setCurrentUser(JSON.parse(storedUser));
      setShowSignInForm(false);
    }
  }, [setToken, setCurrentUser]);

  useEffect(()=>{
    if(!showSignInForm||warmed.current)return;
    warmed.current=true;
    // Public, lightweight warm-up; never await it or send credentials.
    const controller=new AbortController();
    const timer=setTimeout(()=>controller.abort(),8000);
    if(typeof fetch==='function')fetch(`${API_BASE_URL}/health`,{signal:controller.signal,credentials:'omit',cache:'no-store'}).catch(()=>{}).finally(()=>clearTimeout(timer));
    return()=>{clearTimeout(timer);controller.abort();};
  },[showSignInForm]);

  const handleLogin = async (email, password) => {
    if(loginPending.current)return;
    loginPending.current=true;
    setIsLoading(true);
    setErrorMessage('');
    setShowVerifyNotice(false);

    try {
      const response = await ApiService.login(email, password);
      setToken(response.data.access_token);
      // Use full user object from backend response (includes all profile fields)
      const user = response.data.user || { username: response.data.username, id: response.data.user_id };
      setCurrentUser(user);

      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('currentUser', JSON.stringify(user));

      setShowSignInForm(false);
    } catch (error) {
      if (error.response && error.response.data) {
        if (error.response.data.email_not_verified) {
          setShowVerifyNotice(true);
        }
        setErrorMessage(error.response.data.message);
      } else {
        console.error('Error during login:', error);
        setErrorMessage("אירעה שגיאה במהלך הכניסה.");
      }
    } finally {
      loginPending.current=false;
      setIsLoading(false);
    }
  };

  const handleShowSignInForm = () => {
    setShowSignInForm(true);
  };

  const handleCloseSignInForm = () => {
    setShowSignInForm(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLogin(email, password);
    }
  };

  const handleSendReset = async () => {
    if (!resetEmail) {
      setResetMessage("יש להזין כתובת דוא״ל.");
      return;
    }
    setResetMessage('');
    try {
      // Fire-and-forget: Firebase returns 200 even for unknown emails to avoid
      // leaking which accounts exist. Imported lazily to keep the bundle lean.
      const { getAuth, firebaseConfigured } = await import('../firebase');
      const { sendPasswordResetEmail } = await import('firebase/auth');
      if (!firebaseConfigured) {
        setResetMessage("איפוס הסיסמה עדיין אינו זמין. פנו לתמיכה.");
        return;
      }
      const auth = await getAuth();
      if (!auth) {
        setResetMessage("איפוס הסיסמה עדיין אינו זמין. פנו לתמיכה.");
        return;
      }
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage("אם כתובת הדוא״ל רשומה, נשלח אליה קישור לאיפוס הסיסמה.");
    } catch (err) {
      console.error('Password reset error:', err);
      setResetMessage("לא ניתן לשלוח קישור לאיפוס. נסו שוב.");
    }
  };

  return (
    <div className="signin-container">
      {!showSignInForm && (
        <button onClick={handleShowSignInForm}>כניסה</button>
      )}

      {showSignInForm && createPortal((
        <div className="overlay signup-overlay"><div className="signin-page signup-page signup-prestige signin-premium">
          <button aria-label="סגירת חלון הכניסה" onClick={() => { handleCloseSignInForm(); onClose?.(); }} className="close-button">
            <span aria-hidden="true">×</span>
          </button>
          <header className="signup-intro"><span className="signup-eyebrow">CONNEXT · טוב שחזרתם</span><h2>עוד שיחה קצרה.<br/><em>עוד הזדמנות לכימיה.</em></h2><p>חוזרים לספיד דייטינג, בקצב שלכם.</p><div className="signup-intro-line" aria-hidden="true">✦</div></header>
          <div className='user-box'>
            <input
              type="email"
              id="user_email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="דוא״ל"
            />
            <label htmlFor="user_email">דוא״ל</label>
          </div>
          <div className='user-box'>
            <input
              type="password"
              id="pass_word"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="סיסמה"
            />
            <label htmlFor="pass_word">סיסמה</label>
            <button type="button" className="forgot-password-link" onClick={() => setShowReset(true)}>
              שכחתם את הסיסמה?
            </button>
          </div>
          <FloatingError message={errorMessage} onDismiss={() => setErrorMessage('')} />
          {showVerifyNotice && (
            <div className="success-message" style={{ color: '#ffd93d', fontSize: '13px', marginTop: '8px' }}>
              ⚠️ יש לאמת את כתובת הדוא״ל לפני הכניסה. חפשו את קוד האימות בתיבת הדואר.
            </div>
          )}
          <div className='signin-container-button'>
            <button className='btn-1' onClick={() => handleLogin(email, password)} disabled={isLoading}>
              {isLoading ? "נכנסים לחשבון…" : "כניסה"}
            </button>
          </div>

          {isLoading && <ConnectionLoader compact title="נכנסים לחשבון שלכם…" />}
          <SocialAuth onSignIn={() => {}} dividerText="או ממשיכים עם" />

          {/* Password reset */}
          {showReset && (
            <div className="reset-form">
              <h3>איפוס סיסמה</h3>
              <div className='user-box'>
                <input
                  type="email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  placeholder="דוא״ל"
                />
                <label>דוא״ל</label>
              </div>
              {resetMessage && <div className="success-message">{resetMessage}</div>}
              <button className="btn-2" onClick={handleSendReset}>שליחת קישור לאיפוס</button>
              <button type="button" className="link-button" onClick={() => setShowReset(false)}>חזרה לכניסה</button>
            </div>
          )}
        </div></div>
      ),document.body)}
    </div>
  );
}

export default SignIn;
